import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, FileText, ShoppingCart, MessageSquare, Settings,
  Upload, Users, Truck, Receipt, FileCode, Package, X, Menu, Database,
  Calculator, TrendingDown, TrendingUp, Flower2,
} from 'lucide-react';
import { IS_TAURI } from '@/core/environment';
import { extendMenu } from '@/core/vertical-engine';
import { Calendar, Inbox as InboxIcon, Layout, HardHat, Clock } from 'lucide-react';
import { APP_ICON_PATH, APP_NAME } from '@/config/branding';

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Calendar, Inbox: InboxIcon, Layout, HardHat, Calculator, Clock,
  TrendingDown, TrendingUp, Users,
};

const Sidebar = ({ isOpen, toggleSidebar }: { isOpen: boolean; toggleSidebar: () => void }) => {
  const coreNavItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/contacts', icon: Users, label: 'Clientes' },
    { path: '/suppliers', icon: Truck, label: 'Proveedores' },
    { path: '/invoices', icon: FileText, label: 'Facturas' },
    { path: '/quotes', icon: ShoppingCart, label: 'Presupuestos' },
    { path: '/expenses', icon: Receipt, label: 'Gastos' },
    { path: '/items', icon: Package, label: 'Catálogo' },
    { path: '/templates', icon: FileCode, label: 'Plantillas' },
    { path: '/inbox', icon: MessageSquare, label: 'Comunicaciones' },
    { path: '/import', icon: Upload, label: 'Importar' },
    { path: '/settings', icon: Settings, label: 'Configuración' },
  ];

  const verticalItems = extendMenu();

  return (
    <>
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white/80 backdrop-blur-lg rounded-xl shadow-md border border-pink-100"
        onClick={toggleSidebar}
      >
        <Menu size={24} className="text-pink-500" />
      </button>

      <aside
        className={`fixed top-0 left-0 z-40 h-screen bg-white/90 backdrop-blur-xl border-r border-pink-100 transition-transform ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 w-64 flex flex-col`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-pink-50">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative">
              <img src={APP_ICON_PATH} alt="" className="w-11 h-11 rounded-2xl border border-pink-200 bg-white p-1.5 flex-shrink-0 shadow-sm shadow-pink-100" />
              <span className="absolute -top-1 -right-1 text-xs">🌸</span>
            </div>
            <div className="min-w-0">
              <span className="text-xl font-bold text-shimmer whitespace-nowrap block truncate">
                {APP_NAME}
              </span>
            {IS_TAURI && (
              <div className="flex items-center gap-1 mt-0.5">
                <Database size={10} className="text-pink-400" />
                <span className="text-xs text-pink-500 font-medium">SQLite local</span>
              </div>
            )}
            </div>
          </div>
          <button
            className="md:hidden p-1 text-pink-300 hover:text-pink-500"
            onClick={toggleSidebar}
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          <ul className="space-y-0.5">
            {coreNavItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-pink-50 to-rose-50 text-pink-600 shadow-sm shadow-pink-100/50 border border-pink-100/50'
                        : 'text-gray-500 hover:bg-pink-50/50 hover:text-pink-600'
                    }`
                  }
                  onClick={() => window.innerWidth < 768 && toggleSidebar()}
                >
                  <item.icon size={17} className="flex-shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>

          {verticalItems.length > 0 && (
            <>
              <div className="mt-4 mb-2 px-3 flex items-center gap-2">
                <div className="flex-1 h-px bg-gradient-to-r from-pink-100 to-transparent" />
                <span className="text-xs font-semibold text-pink-300 uppercase tracking-wide flex items-center gap-1">
                  <Flower2 size={10} />
                  Módulos
                </span>
                <div className="flex-1 h-px bg-gradient-to-l from-pink-100 to-transparent" />
              </div>
              <ul className="space-y-0.5">
                {verticalItems.map((item) => {
                  const iconStr = typeof item.icon === 'string' ? item.icon : '';
                  const IconComponent = iconStr ? iconMap[iconStr] : undefined;
                  const to = (item as any).to ?? item.path;
                  return (
                    <li key={to}>
                      <NavLink
                        to={to}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                            isActive
                              ? 'bg-gradient-to-r from-pink-50 to-rose-50 text-pink-600 shadow-sm shadow-pink-100/50 border border-pink-100/50'
                              : 'text-gray-500 hover:bg-pink-50/50 hover:text-pink-600'
                          }`
                        }
                        onClick={() => window.innerWidth < 768 && toggleSidebar()}
                      >
                        {IconComponent && <IconComponent size={17} className="flex-shrink-0" />}
                        <span>{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </nav>

        {/* Footer */}
        <div className="border-t border-pink-50 px-4 py-3 bg-gradient-to-r from-pink-50/30 to-transparent">
          <p className="text-xs text-pink-300 flex items-center gap-1.5">
            <span>🌸</span>
            v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '2.0.0'}
            {IS_TAURI ? ' · Desktop' : ' · Web'}
          </p>
        </div>
      </aside>

      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-pink-900/20 backdrop-blur-sm md:hidden"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
};

export default Sidebar;
